import { Checkbox } from '@ordo/ui/checkbox'
import { Input } from '@ordo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'

import type { SenderFormData } from './sender-form'
export { EMPTY_SENDER_FORM, isSenderFormValid, type SenderFormData } from './sender-form'

interface SenderFormFieldsProps {
  data: SenderFormData
  onChange: (field: keyof SenderFormData, value: string | boolean) => void
  disabled?: boolean
  fromEmailHelper?: string
  existing?: boolean
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="text-sm font-medium text-muted-foreground">
      {children}
    </label>
  )
}

export function SenderFormFields({
  data,
  onChange,
  disabled,
  fromEmailHelper,
  existing = false,
}: SenderFormFieldsProps) {
  return (
    <div className="space-y-4">
      {!existing ? (
        <div className="space-y-1.5">
          <FieldLabel htmlFor="sender-method">Channel</FieldLabel>
          <Select value={data.method} onValueChange={(value) => onChange('method', value)} disabled={disabled}>
            <SelectTrigger id="sender-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="EMAIL">Email</SelectItem>
              <SelectItem value="SMS">SMS</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : null}
      <div className="space-y-1.5">
        <FieldLabel htmlFor="sender-nickname">Friendly name</FieldLabel>
        <Input
          id="sender-nickname"
          value={data.nickname}
          disabled={disabled}
          onChange={(e) => onChange('nickname', e.target.value)}
          placeholder={data.method === 'SMS' ? 'e.g. ordo-schedule-sms' : 'e.g. Marketing Newsletter'}
        />
        <p className="text-xs text-muted-foreground">Internal label used inside boombox. Recipients never see this.</p>
      </div>

      {data.method === 'SMS' ? (
        <>
          <div className="space-y-1.5">
            <FieldLabel htmlFor="sender-phone">SMS phone number</FieldLabel>
            <Input
              id="sender-phone"
              type="tel"
              value={data.phoneNumber}
              disabled={disabled || existing}
              onChange={(event) => onChange('phoneNumber', event.target.value)}
              placeholder="Include + and the country code"
            />
            <p className="text-xs text-muted-foreground">
              Use an SMS-capable number already owned by your connected Twilio account.{' '}
              {existing ? 'To change the number, create another sender.' : 'Boombox verifies the number before saving.'}
            </p>
          </div>
          <div className="space-y-1.5">
            <FieldLabel htmlFor="sender-inbound-app">Reply destination</FieldLabel>
            <Select
              value={data.inboundApp || 'none'}
              onValueChange={(value) => onChange('inboundApp', value === 'none' ? '' : value)}
              disabled={disabled}>
              <SelectTrigger id="sender-inbound-app">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Use Inbound settings (if configured)</SelectItem>
                <SelectItem value="schedule">Schedule ambassador messages</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              For other apps, configure forwarding in Inbound. STOP, START and HELP keep their existing preference
              handling.
            </p>
          </div>
          {existing ? (
            <label className="flex items-center gap-2 text-sm" htmlFor="sender-active">
              <Checkbox
                id="sender-active"
                checked={data.active}
                disabled={disabled}
                onCheckedChange={(checked) => onChange('active', checked === true)}
              />
              Active sender
            </label>
          ) : null}
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <FieldLabel htmlFor="sender-from-name">From name</FieldLabel>
              <Input
                id="sender-from-name"
                value={data.fromName}
                disabled={disabled}
                onChange={(e) => onChange('fromName', e.target.value)}
                placeholder="Ordo Schools"
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="sender-from-email">From email</FieldLabel>
              <Input
                id="sender-from-email"
                type="email"
                value={data.fromEmail}
                disabled={disabled}
                onChange={(e) => onChange('fromEmail', e.target.value)}
                placeholder="hello@yourdomain.com"
              />
              {fromEmailHelper ? <p className="text-xs text-muted-foreground">{fromEmailHelper}</p> : null}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <FieldLabel htmlFor="sender-reply-name">Reply-to name</FieldLabel>
              <Input
                id="sender-reply-name"
                value={data.replyToName}
                disabled={disabled}
                onChange={(e) => onChange('replyToName', e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="sender-reply-email">Reply-to email</FieldLabel>
              <Input
                id="sender-reply-email"
                type="email"
                value={data.replyToEmail}
                disabled={disabled}
                onChange={(e) => onChange('replyToEmail', e.target.value)}
                placeholder="replies@yourdomain.com"
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
